import React, { FC, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import {
    clearStatus,
    selectCandidateSignupStatus,
    postCandidateSignupAsync
} from "../../features/candidate/candidateSignupSlice";

import styled from "styled-components";

import Alert from "react-bootstrap/Alert";
import Card from "react-bootstrap/Card";
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from "react-bootstrap/Spinner";

import { Quote } from 'react-bootstrap-icons';

import { RecruiterProfiles } from "../../common/data/recruiter-profiles";


// Job Title Category
const jobTitleCategoryCheckboxes: { [id: string]: {label: string, value: boolean} } = {
    "f8854c2e6a": { "label": "Full Stack", "value": false },
    "659513f0a5": { "label": "Front End", "value": false },
    "581de56b93": { "label": "Backend", "value": false },
    "a17e28b45f": { "label": "DevOps/SRE", "value": false },
    "aea949b7af": { "label": "SecOps/Security", "value": false },
    "a38a80b256": { "label": "Data Engineering", "value": false },
    "3a1b48b11a": { "label": "Machine Learning/Data Science", "value": false },
    "445b65176f": { "label": "Mobile", "value": false },
    "d1ea2ff66e": { "label": "Engineering Leadership", "value": false },
    "110711b415": { "label": "Product", "value": false },
    "32ebdf1ab2": { "label": "Project/Program Management", "value": false },
    "0c6fd66280": { "label": "Systems Engineer/Systems Admin", "value": false },
    "ee3dd0542a": { "label": "Quality Assurance", "value": false }
}

const jobTitleCategoryCheckboxList = [
    "f8854c2e6a",
    "659513f0a5",
    "581de56b93",
    "a17e28b45f",
    "aea949b7af",
    "a38a80b256",
    "3a1b48b11a",
    "445b65176f",
    "d1ea2ff66e",
    "110711b415",
    "32ebdf1ab2",
    "0c6fd66280",
    "ee3dd0542a"
];

// Seniority
const seniorityCheckboxes: { [id: string]: {label: string, value: boolean} } = {
    "063903dab9": { label: 'Mid-Level', value: false },
    "b91be65cef": { label: 'Senior', value: false },
    "9ad50755f3": { label: 'Principal/Staff', value: false },
    "da10e4c8b3": { label: 'Architect', value: false },
    "ac2d041ceb": { label: 'Lead', value: false },
    "ae04d538b8": { label: 'Manager', value: false },
    "0a4a53016c": { label: 'CTO/VP/Director', value: false }
};

const seniorityCheckboxList = [
    "063903dab9",
    "b91be65cef",
    "9ad50755f3",
    "da10e4c8b3",
    "ac2d041ceb",
    "ae04d538b8",
    "0a4a53016c"
];

type CandidateSignupProps = {};

export const CandidateSignup: FC<CandidateSignupProps> = () => {

    const dispatch = useAppDispatch();

    const status = useAppSelector(selectCandidateSignupStatus);

    const queryParams = new URLSearchParams(window.location.search);

    const emailParam = queryParams.get("email");
    const firstName = queryParams.get("first_name");
    const recruiterParam = queryParams.get("recruiter");
    const interestsParam = queryParams.get("interests");

    // Construct the Candidate Interests object to intialize 
    if (interestsParam) {
        const interestTokens = interestsParam.split("-");
        for (var i in interestTokens) {
            const token = interestTokens[i];
            if(jobTitleCategoryCheckboxes[token] !== undefined) {
                jobTitleCategoryCheckboxes[token].value = true;
            } else if (seniorityCheckboxes[token] !== undefined) {
                seniorityCheckboxes[token].value = true;
            }
        }
    }

    const errorAlertMessage = "I am very sorry. There seems to have been an error."
    const successAlertMessage = "Thank you for signing up."


    // Email field hooks
    const [email, setEmail] = useState(emailParam);
    const emailValue = String(email) || "";

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
	}

    // Job Title Category
    const [jobTitleCategoryInterests, setJobTitleCategoryInterests] = useState(jobTitleCategoryCheckboxes);

    const handleJobTitleCategoryInterestsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setJobTitleCategoryInterests(prevState => ({...prevState, [e.target.id]: { label: jobTitleCategoryCheckboxes[e.target.id].label, value: e.target.checked }}));
    }

    // Seniority
    const [seniorityInterests, setSeniorityInterests] = useState(seniorityCheckboxes);

    const handleSeniorityInterestsChange  = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSeniorityInterests(prevState => ({...prevState, [e.target.id]: { label: seniorityCheckboxes[e.target.id].label, value: e.target.checked }}));
    }

    const submitHandler = (event: React.FormEvent) => {
        event.preventDefault();

        // Combine all intersts in to single object
        let combinedInterestCheckboxes = {
            ...jobTitleCategoryInterests,
            ...seniorityInterests
        }

        let combinedInterests : {[id: string]: boolean} = {}; 

        for (var interest in combinedInterestCheckboxes) {
            combinedInterests[interest] = combinedInterestCheckboxes[interest].value;
        }

        dispatch(postCandidateSignupAsync({email: emailValue, interests: combinedInterests}));
    }

    return (
        <>
            <Container>
                {status === "failed" &&
                    <Alert variant="danger" onClose={ () => dispatch(clearStatus()) } dismissible>
                        {errorAlertMessage}
                    </Alert>
                }
                {status === "success" &&
                    <Alert variant="success" onClose={ () => dispatch(clearStatus()) } dismissible>
                        {successAlertMessage}
                    </Alert>
                }
                <ContentSection className="w-100 m-auto">
                    <Form onSubmit={submitHandler}>
                        <h1 className="mb-3 fw-normal">Hello{firstName ? " " + firstName : ""}!</h1>
                        <h5 className="mb-3 fw-normal">Thank you for wanting to stay in touch.</h5>

                        { recruiterParam && RecruiterProfiles[recruiterParam] &&
                            <Card className="mb-4 bg-light shadow-sm">
                                <Card.Body className="text-center">
                                    <Card.Img className={"rounded-circle shadow mx-auto mt-2"} variant={"top"} src= { RecruiterProfiles[recruiterParam].headshot } style={{maxWidth: '200px'}} />
                                    <blockquote className="blockquote mb-0">
                                        <p>
                                            <Quote className="me-2" />
                                            { RecruiterProfiles[recruiterParam].candidate_quote }
                                        </p>
                                        <footer className="blockquote-footer">
                                            { RecruiterProfiles[recruiterParam].display_name + ", " + RecruiterProfiles[recruiterParam].position }
                                        </footer>
                                    </blockquote>
                                </Card.Body>
                            </Card>
                        }
                        
                        <h5 className="mb-3 fw-normal">Please confirm the details below, so we can send you only relevant information.</h5>

                        <Form.Group className="mb-4" controlId="formBasicEmail">
                            <Form.Control required type="email" placeholder="Please enter your email" value={email || ''} onChange={ handleEmailChange } />
                            <Form.Text className="text-muted">
                                Your preferred contact email
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicCheckbox">
                            <h5 className="mb-3 fw-normal">Job title categories you are intersted in</h5>
                            {jobTitleCategoryCheckboxList.map((checkbox, idx) => {
                                return (
                                    <Form.Check id={checkbox} key={checkbox} type="checkbox" label={jobTitleCategoryInterests[checkbox].label} checked={jobTitleCategoryInterests[checkbox].value} onChange={handleJobTitleCategoryInterestsChange} />
                                );
                            })}
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicCheckbox">
                            <h5 className="mb-3 fw-normal">Your desired seniority level</h5>
                            {seniorityCheckboxList.map((checkbox, idx) => {
                                return (
                                    <Form.Check id={checkbox} key={checkbox} type="checkbox" label={seniorityInterests[checkbox].label} checked={seniorityInterests[checkbox].value} onChange={handleSeniorityInterestsChange} />
                                );
                            })}
                        </Form.Group>

                        <Button variant="primary" type="submit" disabled={ status === 'loading' ? true : false }>
                            { status === 'loading' &&
                                <div>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    <span className="visually-hidden">Loading...</span> 
                                </div>
                            }
                            {status === 'loading' ? "" : "Submit"}
                        </Button>

                        {/* <a className="mt-3 link-secondary d-block" href="#">Opt out of all email comminications</a> */}
                    </Form>
                </ContentSection>
            </Container>
        </>
    );
}

const ContentSection = styled.div`
    max-width: 700px;
    padding: 15px;
`